import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { LayoutGrid, Users, Building2, Calculator, WalletCards, Sparkles, Ruler, LifeBuoy, Fingerprint, Lock, Check, ChevronDown, ExternalLink, Sparkle, CreditCard } from "lucide-react";
import { PROGERE_MODULES, MODULE_BY_CODE } from "../config/progereModules";
import { PROGERE_MODULE } from "../config/progereModule";
import { ProgereDownloadAppButton } from "./ProgereDownloadAppButton";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICONS = { LayoutGrid, Users, Building2, Calculator, WalletCards, Sparkles, Ruler, LifeBuoy, Fingerprint };
const STATUS = {
  active: { label: "INCLUS", cls: "ok" },
  free: { label: "GRATUIT", cls: "ok" },
  locked: { label: "VERROUILLÉ", cls: "locked" },
  soon: { label: "BIENTÔT", cls: "soon" },
};

export function AppSwitcher() {
  const [open, setOpen] = useState(false);
  const [ent, setEnt] = useState(null); // droits serveur { code: {status, can_subscribe, can_trial, domain} }
  const ref = useRef(null);
  const { meta } = useAuth();
  const current = MODULE_BY_CODE[PROGERE_MODULE.code] || MODULE_BY_CODE.punch;
  const others = PROGERE_MODULES.filter((m) => m.code !== PROGERE_MODULE.code);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (open && !ent) {
      api.ecosystemModules()
        .then((d) => setEnt(Object.fromEntries((d.modules || []).map((m) => [m.code, m]))))
        .catch(() => setEnt({}));
    }
  }, [open, ent]);

  const statusOf = (code) => ent?.[code]?.status || (code === "punch" || code === "core" || code === "support" ? "active" : "soon");

  const openModule = (m, e) => {
    e?.stopPropagation();
    setOpen(false);
    window.open(m.domain, "_blank", "noopener");
  };
  const discover = (m, e) => {
    e?.stopPropagation();
    const st = statusOf(m.code);
    if (st === "soon") { toast.info(`${m.name} — bientôt disponible`, { description: m.desc }); return; }
    window.open(m.domain, "_blank", "noopener");
  };
  const subscribe = (m, e) => { e?.stopPropagation(); setOpen(false); window.location.href = "/abonnement"; };
  const tryModule = (m, e) => { e?.stopPropagation(); setOpen(false); window.location.href = "/abonnement"; };

  const CurIcon = ICONS[current.icon] || LayoutGrid;

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className={`app-switcher-trigger ${open ? "open" : ""}`} onClick={() => setOpen((v) => !v)} data-testid="app-switcher-toggle" title="Applications PROGÈRE">
        <LayoutGrid size={16} /> <span className="ast-label">Applications <b>PROGÈRE</b></span> <ChevronDown size={15} className="ast-chev" />
      </button>

      {open && (
        <div className="app-switcher-panel" data-testid="app-switcher-menu">
          <div className="asp-head">
            <div>
              <div className="asp-brand"><span className="gold">PROG</span><span className="red">ÈRE</span></div>
              <div className="asp-sub">VOTRE ÉCOSYSTÈME D'ENTREPRISE</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="asp-company" title={meta?.company}>{meta?.company || "Votre entreprise"}</div>
              <div className="asp-connected"><span className="dot" /> Connecté</div>
            </div>
          </div>

          <div className="asp-section-label">MODULE ACTUEL</div>
          <div className="asp-current" data-testid="app-current-module">
            <div className="asp-ico gold"><CurIcon size={20} /></div>
            <div style={{ flex: 1 }}>
              <div className="asp-current-name">PROGÈRE {current.short}</div>
              <div className="asp-desc">{current.desc}</div>
            </div>
            <span className="asp-badge active">ACTIF</span>
          </div>

          <div className="asp-section-label">VOS APPLICATIONS</div>
          <div className="asp-grid">
            {others.map((m) => {
              const Icon = ICONS[m.icon] || LayoutGrid;
              const st = statusOf(m.code);
              const info = STATUS[st] || STATUS.soon;
              const e = ent?.[m.code] || {};
              const unlocked = st === "active" || st === "free";
              return (
                <div key={m.code} className={`asp-card ${unlocked ? "" : "locked"}`} data-testid={`app-tile-${m.code}`}>
                  <span className="asp-card-top">{unlocked ? <Check size={14} className="asp-check" /> : <Lock size={13} className="asp-lock" />}</span>
                  <div className={`asp-ico ${m.color}`}><Icon size={18} /></div>
                  <div className="asp-card-name">{m.short}</div>
                  <div className="asp-desc">{m.desc}</div>
                  <span className={`asp-badge ${info.cls}`}>{info.label}</span>

                  <div className="asp-actions">
                    {unlocked ? (
                      <button className="asp-act primary" onClick={(ev) => openModule(m, ev)} data-testid={`app-open-${m.code}`}>
                        <ExternalLink size={12} /> Ouvrir
                      </button>
                    ) : (
                      <>
                        <button className="asp-act" onClick={(ev) => discover(m, ev)} data-testid={`app-discover-${m.code}`}>Découvrir</button>
                        {e.can_trial && (
                          <button className="asp-act" onClick={(ev) => tryModule(m, ev)} data-testid={`app-try-${m.code}`}>
                            <Sparkle size={11} /> Essayer
                          </button>
                        )}
                        {e.can_subscribe && (
                          <button className="asp-act primary" onClick={(ev) => subscribe(m, ev)} data-testid={`app-subscribe-${m.code}`}>
                            <CreditCard size={11} /> S'abonner
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="asp-download">
            <ProgereDownloadAppButton variant="compact" size="sm" moduleCode="punch" />
          </div>
        </div>
      )}
    </div>
  );
}
